import React from "react";
import { LegalPage, H2, P, UL } from "./LegalPage";

export const CommunityGuidelines = () => (
  <LegalPage
    eyebrow="Community"
    title="Community Guidelines"
    description="How we keep Kerala Speaks respectful, useful, and safe — together."
    canonical="/community-guidelines"
    lastUpdated="May 2026"
    testId="guidelines-page"
  >
    <P>
      Kerala Speaks works because real citizens show up and speak honestly. These guidelines exist to keep the conversation respectful, useful, and worth your time. They apply to every message, vote, follow-up comment, and reply on the platform.
    </P>

    <H2>★ The short version</H2>
    <P>
      <strong>Speak your mind. Don't attack people. Stay honest.</strong> That's most of it.
    </P>

    <H2>1. Be respectful</H2>
    <UL>
      <li>Disagree with ideas, not with people.</li>
      <li>Treat every district, religion, language, caste, and community with the same respect you would want for your own.</li>
      <li>Assume good faith. Many citizens are participating in civic conversation for the first time.</li>
    </UL>

    <H2>2. Stay civic and constructive</H2>
    <UL>
      <li>Focus on issues — roads, healthcare, jobs, education, public safety, environment — not personalities.</li>
      <li>If you're criticising a decision, point to a better alternative when you can.</li>
      <li>Lived experience is welcome. "This is what I see every day in my neighbourhood" is exactly the kind of voice this platform exists for.</li>
    </UL>

    <H2>3. Things that will be removed</H2>
    <UL>
      <li><strong>Hate speech</strong> against any community, caste, religion, gender, or region.</li>
      <li><strong>Personal attacks, slurs, or doxxing.</strong></li>
      <li><strong>Threats of violence</strong> or content that glorifies violence.</li>
      <li><strong>Misinformation</strong> presented as fact — especially around health, elections, or public safety.</li>
      <li><strong>Political campaigning</strong> for or against any specific party, candidate, or election campaign.</li>
      <li><strong>Spam, advertising, affiliate links</strong>, or copy-pasted bulk content.</li>
      <li><strong>Sexually explicit content</strong> or content sexualising minors.</li>
      <li><strong>Impersonation</strong> of public figures, journalists, or other citizens.</li>
    </UL>

    <H2>4. How moderation works</H2>
    <UL>
      <li>Every submission passes through automated checks for spam, abuse, and known harmful phrases.</li>
      <li>Borderline content is flagged for human review before it appears publicly.</li>
      <li>Approved content can be hidden later if community reports surface a problem we missed.</li>
      <li>We err on the side of allowing genuine civic speech — even when it's uncomfortable — and on the side of removing content that targets people rather than ideas.</li>
    </UL>

    <H2>5. If your content was removed</H2>
    <P>
      We sometimes get it wrong. If you believe your submission was removed by mistake, email <a className="underline" href="mailto:keralaspeaks7@gmail.com">keralaspeaks7@gmail.com</a> with the message text and we'll review it. We aim to respond within 5 business days.
    </P>

    <H2>6. If you see something</H2>
    <P>
      Use the report option on any message, or email <a className="underline" href="mailto:keralaspeaks7@gmail.com">keralaspeaks7@gmail.com</a> with a link or screenshot. Reports are confidential.
    </P>

    <H2>7. Repeated violations</H2>
    <P>
      Accounts that repeatedly violate these guidelines may have new submissions auto-held for review or be blocked from posting entirely. Bans are decided case-by-case and prioritise the safety of other participants.
    </P>

    <H2>8. A reminder</H2>
    <P>
      Kerala Speaks is non-partisan and independent. The most useful contribution you can make is to <strong>tell the truth about what you see</strong> and <strong>invite others to do the same.</strong> Thank you for being here.
    </P>
  </LegalPage>
);

export default CommunityGuidelines;
